/**
 * /printers — Printer registry management page.
 *
 * Users save named printers (IP/host, port, linked printer profile),
 * test connectivity, and manage them here.
 *
 * This page is DESIGNER-LOCAL: printers are stored in localStorage and are
 * NOT on the Host Contract / adapter seam. This is the deletable seam when a
 * future server-side executor takes over.
 */
import { getPrinterProfiles } from '@tsagko/label-printer-profiles'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfirm } from '../ui/ConfirmDialog'
import { listPrinters, type SavedPrinter, usePrinters } from './printer-store'

// ---------------------------------------------------------------------------
// Host validation helpers
// ---------------------------------------------------------------------------

const PRIVATE_IPV4_RE =
  /^(?:10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(?:1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|127\.\d{1,3}\.\d{1,3}\.\d{1,3})$/

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/

/** Returns null (valid) or an error message string. */
function validateHost(host: string): string | null {
  const trimmed = host.trim()
  if (!trimmed) return 'Host is required.'
  if (IPV4_RE.test(trimmed)) {
    // It's an IPv4 address — must be private or loopback.
    if (!PRIVATE_IPV4_RE.test(trimmed)) {
      return 'Public IP addresses are not allowed. Only private (10.x, 172.16-31.x, 192.168.x) or loopback (127.x) addresses are permitted.'
    }
  }
  // Hostname strings (non-IP) are accepted but get a hint — they require
  // server-side allowlisting via PRINT_ALLOWED_HOSTS.
  return null
}

/** Returns a hint string when the host is a non-IP hostname (allowlist reminder). */
function hostnameHint(host: string): string | null {
  const trimmed = host.trim()
  if (!trimmed) return null
  if (IPV4_RE.test(trimmed)) return null // IP address — no hint needed
  return 'Hostname addresses must be allowlisted server-side via PRINT_ALLOWED_HOSTS.'
}

// ---------------------------------------------------------------------------
// Profile / device-info helpers
// ---------------------------------------------------------------------------

interface DeviceInfo {
  model?: string
  firmware?: string
  dpmm?: number
  dpi?: number
}

/** "{dpi} dpi — {brand} {model}" — DPI-first label for profile selection. */
function profileLabel(p: { dpi: number; brand: string; model: string }): string {
  return `${p.dpi} dpi — ${p.brand} ${p.model}`
}

/** Human summary of a ~HI device reply, e.g. "ZT411-300dpi · 300 dpi". */
function deviceSummary(device: DeviceInfo): string | null {
  const parts: string[] = []
  if (device.model) parts.push(device.model)
  if (device.dpi != null) parts.push(`${device.dpi} dpi`)
  else if (device.dpmm != null) parts.push(`${device.dpmm} dpmm`)
  return parts.length > 0 ? parts.join(' · ') : null
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TestResult {
  printerId: string
  reachable: boolean
  message: string
}

interface FormState {
  name: string
  host: string
  port: string
  profileId: string
}

const DEFAULT_FORM: FormState = {
  name: '',
  host: '',
  port: '9100',
  profileId: '',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PrintersRoute() {
  const { printers, save, remove } = usePrinters()
  const confirm = useConfirm()
  // Only the destructive confirm is keyed here — the rest of this route is
  // covered by the printing-surface sweep, not the block-configurator half.
  const { t } = useTranslation()
  const profiles = useMemo(() => getPrinterProfiles(), [])

  // DPI-first ordering: profiles are picked by resolution before model.
  const sortedProfiles = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          a.dpi - b.dpi ||
          `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`)
      ),
    [profiles]
  )

  // In-memory test results (session-only, not persisted).
  const [testResults, setTestResults] = useState<Map<string, TestResult>>(new Map())
  const [testingIds, setTestingIds] = useState<Set<string>>(new Set())

  // Form state: null = form hidden, string = id being edited ('' for new)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({})
  const formRef = useRef<HTMLDivElement>(null)

  const defaultProfileId = profiles[0]?.id ?? ''

  function openAddForm() {
    setEditingId('')
    setForm({ ...DEFAULT_FORM, profileId: defaultProfileId })
    setFormErrors({})
  }

  function openEditForm(printer: SavedPrinter) {
    setEditingId(printer.id)
    setForm({
      name: printer.name,
      host: printer.host,
      port: String(printer.port),
      profileId: printer.profileId,
    })
    setFormErrors({})
  }

  function closeForm() {
    setEditingId(null)
    setForm(DEFAULT_FORM)
    setFormErrors({})
  }

  function handleFormChange(field: keyof FormState, value: string) {
    setForm(prev => ({ ...prev, [field]: value }))
    // Clear field error on change.
    setFormErrors(prev => ({ ...prev, [field]: undefined }))
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {}

    if (!form.name.trim()) {
      errors.name = 'Name is required.'
    }

    const hostError = validateHost(form.host)
    if (hostError) {
      errors.host = hostError
    }

    const port = Number(form.port)
    if (!form.port.trim() || !Number.isInteger(port) || port < 1 || port > 65535) {
      errors.port = 'Port must be an integer between 1 and 65535.'
    }

    if (!form.profileId) {
      errors.profileId = 'A printer profile is required.'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  function handleSave() {
    if (!validateForm()) return

    const isNew = editingId === ''
    const id = isNew ? crypto.randomUUID() : (editingId as string)
    const existing = isNew ? undefined : printers.find(p => p.id === id)
    const host = form.host.trim()
    // Detected device info describes a specific physical printer — keep it
    // across edits, but drop it when the record is re-pointed at another host.
    const keepDetected = existing != null && existing.host === host

    save({
      id,
      name: form.name.trim(),
      host,
      port: Number(form.port),
      profileId: form.profileId,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      detectedDpi: keepDetected ? existing.detectedDpi : undefined,
      detectedModel: keepDetected ? existing.detectedModel : undefined,
    })
    closeForm()
  }

  async function handleDelete(printer: SavedPrinter) {
    const confirmed = await confirm({
      title: t('confirm.deletePrinter.title', { name: printer.name }),
      body: t('confirm.deletePrinter.body'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    })
    if (!confirmed) return
    remove(printer.id)
    setTestResults(prev => {
      const next = new Map(prev)
      next.delete(printer.id)
      return next
    })
  }

  async function handleTest(printer: SavedPrinter) {
    const backendUrl = import.meta.env.VITE_RENDER_BACKEND_URL
    const apiKey = import.meta.env.VITE_RENDER_API_KEY

    if (!backendUrl || !apiKey) {
      setTestResults(prev =>
        new Map(prev).set(printer.id, {
          printerId: printer.id,
          reachable: false,
          message: 'RENDER_BACKEND_NOT_CONFIGURED: VITE_RENDER_BACKEND_URL and VITE_RENDER_API_KEY must both be set',
        })
      )
      return
    }

    setTestingIds(prev => new Set(prev).add(printer.id))
    try {
      const res = await fetch(`${backendUrl}/api/v1/print/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({ printer: { host: printer.host, port: printer.port } }),
      })
      if (res.ok) {
        const body = (await res.json()) as {
          reachable: boolean
          statusRaw?: string
          device?: DeviceInfo
        }
        const summary = body.device ? deviceSummary(body.device) : null
        setTestResults(prev =>
          new Map(prev).set(printer.id, {
            printerId: printer.id,
            reachable: body.reachable,
            message: body.reachable
              ? `Reachable${summary ? ` — ${summary}` : body.statusRaw ? ` — ${body.statusRaw}` : ''}`
              : `Unreachable${body.statusRaw ? `: ${body.statusRaw}` : ''}`,
          })
        )
        // Persist what the printer reported. A fresh ~HI reply is
        // authoritative; absence of a reply (transient silence) must NOT
        // wipe previously-detected info. Re-read the live record first: the
        // probe takes seconds and `printer` is a click-time snapshot — saving
        // it would revert concurrent edits or resurrect a deleted printer.
        if (body.device && (body.device.dpi != null || body.device.model)) {
          const live = listPrinters().find(p => p.id === printer.id)
          if (live) {
            save({
              ...live,
              detectedDpi: body.device.dpi,
              detectedModel: body.device.model,
            })
          }
        }
      } else {
        const body = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string } }
          | null
        const msg = body?.error?.message ?? `HTTP ${res.status}`
        setTestResults(prev =>
          new Map(prev).set(printer.id, {
            printerId: printer.id,
            reachable: false,
            message: `Unreachable: ${msg}`,
          })
        )
      }
    } catch (e) {
      setTestResults(prev =>
        new Map(prev).set(printer.id, {
          printerId: printer.id,
          reachable: false,
          message: `Unreachable: ${e instanceof Error ? e.message : 'Network error'}`,
        })
      )
    } finally {
      setTestingIds(prev => {
        const next = new Set(prev)
        next.delete(printer.id)
        return next
      })
    }
  }

  const hint = form.host ? hostnameHint(form.host) : null

  return (
    <div className="h-full overflow-auto p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Printers</h2>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {printers.length === 1 ? '1 saved printer' : `${printers.length} saved printers`}
            {' · stored locally in this browser'}
          </p>
        </div>
        {editingId === null && (
          <button
            type="button"
            data-testid="printers-add"
            onClick={openAddForm}
            className="rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            + Add printer
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {editingId !== null && (
        <div
          ref={formRef}
          className="mb-6 rounded border border-stone-200 bg-white p-5 shadow-sm dark:border-stone-800 dark:bg-stone-900"
        >
          <h3 className="mb-4 text-sm font-semibold">
            {editingId === '' ? 'Add printer' : 'Edit printer'}
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="printer-form-name"
                type="text"
                value={form.name}
                onChange={e => handleFormChange('name', e.target.value)}
                placeholder="e.g. Warehouse A"
                className="w-full rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-950"
              />
              {formErrors.name && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors.name}</p>
              )}
            </div>

            {/* Host */}
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
                Host <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="printer-form-host"
                type="text"
                value={form.host}
                onChange={e => handleFormChange('host', e.target.value)}
                placeholder="192.168.1.100"
                className="w-full rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-950"
              />
              {formErrors.host && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors.host}</p>
              )}
              {!formErrors.host && hint && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{hint}</p>
              )}
            </div>

            {/* Port */}
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
                Port <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="printer-form-port"
                type="number"
                min={1}
                max={65535}
                value={form.port}
                onChange={e => handleFormChange('port', e.target.value)}
                className="w-full rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-950"
              />
              {formErrors.port && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors.port}</p>
              )}
            </div>

            {/* Profile */}
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
                Printer profile <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="printer-form-profile"
                value={form.profileId}
                onChange={e => handleFormChange('profileId', e.target.value)}
                className="w-full rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-950"
              >
                <option value="">— select a profile —</option>
                {sortedProfiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {profileLabel(p)}
                  </option>
                ))}
              </select>
              {formErrors.profileId && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{formErrors.profileId}</p>
              )}
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="rounded border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-200 dark:hover:bg-stone-700"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="printer-form-save"
              onClick={handleSave}
              className="rounded bg-stone-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Printer list */}
      {printers.length === 0 && editingId === null ? (
        <div className="rounded border border-dashed border-stone-300 p-12 text-center dark:border-stone-700">
          <p className="text-sm text-stone-600 dark:text-stone-400">
            No printers saved yet. Add your first printer to start printing.
          </p>
          <button
            type="button"
            data-testid="printers-add"
            onClick={openAddForm}
            className="mt-4 rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            + Add printer
          </button>
        </div>
      ) : printers.length > 0 ? (
        <div className="overflow-hidden rounded border border-stone-200 dark:border-stone-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs text-stone-500 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-400">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Host : Port</th>
                <th className="px-4 py-2 font-medium">Profile</th>
                <th className="px-4 py-2 font-medium">Last test</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {printers.map(printer => {
                const result = testResults.get(printer.id)
                const isTesting = testingIds.has(printer.id)
                const profile = profiles.find(p => p.id === printer.profileId)

                // DPI mismatch: the printer reported a resolution that differs
                // from the linked profile — ZPL generated with this profile
                // would print at the wrong physical size.
                const dpiMismatch =
                  printer.detectedDpi != null &&
                  profile != null &&
                  printer.detectedDpi !== profile.dpi
                const dpiCandidates = dpiMismatch
                  ? sortedProfiles.filter(p => p.dpi === printer.detectedDpi)
                  : []
                // Narrow by detected model when several profiles share the
                // detected DPI (e.g. "ZT411-300dpi" → Zebra ZT411).
                const modelMatches =
                  printer.detectedModel != null
                    ? dpiCandidates.filter(p =>
                        printer
                          .detectedModel!.toUpperCase()
                          .includes(p.model.toUpperCase())
                      )
                    : []
                const exactMatch =
                  dpiCandidates.length === 1
                    ? dpiCandidates[0]
                    : modelMatches.length === 1
                      ? modelMatches[0]
                      : undefined

                return (
                  <tr
                    key={printer.id}
                    data-testid={`printer-row-${printer.id}`}
                    className="bg-white hover:bg-stone-50 dark:bg-stone-950 dark:hover:bg-stone-900"
                  >
                    <td className="px-4 py-3 font-medium">{printer.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-stone-600 dark:text-stone-400">
                      {printer.host}:{printer.port}
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600 dark:text-stone-400">
                      {profile?.name ?? printer.profileId}
                      {dpiMismatch && profile && (
                        <div
                          data-testid={`printer-dpi-mismatch-${printer.id}`}
                          className="mt-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300"
                        >
                          <p className="font-medium">
                            ⚠ Printer reports {printer.detectedDpi} dpi but the
                            linked profile is {profile.dpi} dpi — printed size
                            would be wrong.
                          </p>
                          {exactMatch ? (
                            <button
                              type="button"
                              data-testid={`printer-dpi-switch-${printer.id}`}
                              onClick={() =>
                                save({ ...printer, profileId: exactMatch.id })
                              }
                              className="mt-1 rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:bg-stone-900 dark:text-amber-300 dark:hover:bg-amber-900"
                            >
                              Switch to {profileLabel(exactMatch)}
                            </button>
                          ) : dpiCandidates.length > 0 ? (
                            <button
                              type="button"
                              data-testid={`printer-dpi-edit-${printer.id}`}
                              onClick={() => openEditForm(printer)}
                              className="mt-1 rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:bg-stone-900 dark:text-amber-300 dark:hover:bg-amber-900"
                            >
                              Edit to pick a {printer.detectedDpi} dpi profile (
                              {dpiCandidates.length} available)
                            </button>
                          ) : (
                            <p className="mt-1">
                              No {printer.detectedDpi} dpi profile is available.
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {isTesting ? (
                        <span className="text-stone-400">Testing…</span>
                      ) : result ? (
                        <span
                          className={
                            result.reachable
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                          }
                        >
                          {result.reachable ? '✓ ' : '✗ '}
                          {result.message}
                        </span>
                      ) : (
                        <span className="text-stone-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          data-testid={`printer-test-${printer.id}`}
                          disabled={isTesting}
                          onClick={() => void handleTest(printer)}
                          className="rounded border border-stone-200 bg-white px-2.5 py-1 text-xs hover:bg-stone-50 disabled:opacity-50 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
                        >
                          Test
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditForm(printer)}
                          className="rounded border border-stone-200 bg-white px-2.5 py-1 text-xs hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          data-testid={`printer-delete-${printer.id}`}
                          onClick={() => void handleDelete(printer)}
                          className="rounded border border-rose-200 bg-white px-2.5 py-1 text-xs text-rose-600 hover:bg-rose-50 dark:border-rose-900 dark:bg-stone-800 dark:text-rose-400 dark:hover:bg-rose-950"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
